import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import {
  Shield, ArrowLeft, Database, CheckCircle2, AlertTriangle,
  XCircle, RefreshCw, WifiOff, Terminal, Lock, Cpu, Activity,
  Users, Pencil, Trash2, Eye, EyeOff, Save, X, UserPlus, Search,
  Server, Table, FlaskConical, ExternalLink,
  Share2, BookOpen, ChevronDown, ChevronRight,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';

// Password input with show/hide toggle
function PasswordInput({ value, onChange, placeholder, error }: { value: string; onChange: (v: string) => void; placeholder: string; error?: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2 pr-10 rounded-lg text-sm text-white bg-slate-800 border focus:outline-none ${
          error ? 'border-rose-500 focus:border-rose-500' : 'border-slate-700 focus:border-rb-brand'
        }`}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}
import { toast } from 'sonner';
import {
  hasPhpBackend, apiCreateUser, apiListUsers, apiDeleteUser, getApiBase,
  apiAdminSamplePool, apiAdminGetAssignments, apiAdminSaveAssignments,
} from '@/lib/phpApi';
import { getOriginalFetch } from '@/lib/fetchInterceptor';
import { seedUserDataRemote, seedUserDataLocally } from '@/lib/seedUserData';

interface HealthCheck {
  status: string;
  responseTimeMs: number;
  checks?: Record<string, { pass?: boolean; value?: string | boolean }>;
  errors?: string[];
  suggestions?: string[];
  phpVersion?: string;
}

interface LocalUser {
  id: string;
  username: string;
  name: string | null;
  role: string;
  createdAt: string;
  password?: string;
  inDatabase?: boolean; // true = confirmed in MySQL, false = localStorage only
}

interface SamplePoolItem {
  id: number; ownerUserId: number; customerId: number; siteId: number;
  sampleName: string; reportNumber: string; standard: string; sampleType: string;
  ownerUsername: string; ownerRole: string;
  customerName: string; customerCompany: string; siteName: string;
  notes: string;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'health' | 'users' | 'demo' | 'assignments'>('health');

  // ─── Health State ───
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [loading, setLoading] = useState(false);
  const [dbUrl, setDbUrl] = useState(import.meta.env.VITE_API_URL || '');

  // ─── User Management State ───
  const [localUsers, setLocalUsers] = useState<LocalUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<'user' | 'admin'>('user');
  const [resetPwUser, setResetPwUser] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [seedingUser, setSeedingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ username: '', password: '', confirmPassword: '', name: '', role: 'user' as 'user' | 'admin', country: 'GB' });

  // ─── Sample Assignments State ───
  const [assignTargetUser, setAssignTargetUser]     = useState<LocalUser | null>(null);
  const [samplePool, setSamplePool]                 = useState<SamplePoolItem[]>([]);
  const [assignedIds, setAssignedIds]               = useState<Set<number>>(new Set());
  const [loadingPool, setLoadingPool]               = useState(false);
  const [savingAssignments, setSavingAssignments]   = useState(false);
  const [poolSearch, setPoolSearch]                 = useState('');
  const [expandedOwners, setExpandedOwners]         = useState<Set<string>>(new Set());

  // ─── Load sample pool + current assignments for chosen user ───
  const loadAssignments = useCallback(async (targetUser: LocalUser) => {
    setLoadingPool(true);
    setSamplePool([]);
    setAssignedIds(new Set());
    try {
      // Run both in parallel; handle each independently so one failure doesn't kill the other
      const [poolResult, assignResult] = await Promise.allSettled([
        apiAdminSamplePool(),
        apiAdminGetAssignments(Number(targetUser.id)),
      ]);

      if (poolResult.status === 'fulfilled' && poolResult.value.success) {
        const samples = poolResult.value.samples || [];
        setSamplePool(samples);
        const owners = new Set<string>(samples.map((s: SamplePoolItem) => s.ownerUsername));
        setExpandedOwners(owners);
      } else if (poolResult.status === 'rejected') {
        toast.error('Sample pool error: ' + poolResult.reason?.message);
      }

      if (assignResult.status === 'fulfilled' && assignResult.value.success) {
        const ids = new Set<number>((assignResult.value.assignments || []).map((a: any) => Number(a.sample_id)));
        setAssignedIds(ids);
      }
      // If assignments call failed (table not yet created), just leave assignedIds empty — that's fine
    } catch (e: any) {
      toast.error('Failed to load: ' + (e?.message || 'unknown error'));
    } finally {
      setLoadingPool(false);
    }
  }, []);

  const saveAssignments = async () => {
    if (!assignTargetUser) return;
    setSavingAssignments(true);
    try {
      // Only assign samples NOT already owned by the target user
      const ownIds = new Set(samplePool.filter(s => String(s.ownerUserId) === assignTargetUser.id).map(s => s.id));
      const toAssign = [...assignedIds].filter(id => !ownIds.has(id));
      const data = await apiAdminSaveAssignments(Number(assignTargetUser.id), toAssign);
      if (data.success) {
        toast.success(`${data.assigned} sample(s) shared with ${assignTargetUser.username}`);
      }
    } catch (e: any) {
      toast.error('Failed to save: ' + (e?.message || 'unknown error'));
    } finally {
      setSavingAssignments(false);
    }
  };

  const toggleSample = (id: number) => {
    setAssignedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleOwnerGroup = (owner: string, sampleIds: number[]) => {
    const allChecked = sampleIds.every(id => assignedIds.has(id));
    setAssignedIds(prev => {
      const next = new Set(prev);
      if (allChecked) sampleIds.forEach(id => next.delete(id));
      else            sampleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const checkHealth = useCallback(async () => {
    setLoading(true);
    const url = dbUrl ? `${dbUrl}/health/check.php` : `${getApiBase()}/health/check.php`;
    try {
      // Use original fetch to bypass interceptor — we need raw response for diagnostics
      const rawFetch = getOriginalFetch();
      const res = await rawFetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
      const text = await res.text();
      let data: any;
      try {
        data = JSON.parse(text);
      } catch {
        // Not JSON — server returned HTML or plain text
        setHealth({
          status: 'unreachable',
          responseTimeMs: 0,
          checks: {},
          errors: [`Server returned ${res.status} with non-JSON body (length: ${text.length})`],
          diagnostics: {
            url,
            status: res.status,
            contentType: res.headers.get('content-type') || 'unknown',
            bodyPreview: text.substring(0, 300).replace(/\s+/g, ' '),
            isHtml: text.trimStart().startsWith('<'),
          },
          suggestions: text.trimStart().startsWith('<')
            ? [
                'CRITICAL: The server is rewriting API requests to index.html.',
                'If using Apache: ensure .htaccess is enabled (AllowOverride All)',
                'If using nginx: add the nginx-php-subdir.conf rules (see deploy-guide/)',
                'If using cPanel: contact your host to enable mod_rewrite and .htaccess',
              ]
            : [
                'Upload the api/ folder to your web server',
                'Ensure PHP 8.1+ is active in cPanel',
                'Check api/health/check.php exists on the server',
              ],
        });
        toast.error('Backend unreachable — API requests are being rewritten');
        setLoading(false);
        return;
      }
      setHealth(data);
      if (data.status === 'healthy') {
        toast.success('Backend is healthy');
      } else {
        toast.warning(`Backend degraded: ${data.errors?.length || 0} issues`);
      }
    } catch (err: any) {
      setHealth({
        status: 'unreachable',
        responseTimeMs: 0,
        checks: {},
        errors: ['Network error: ' + (err?.message || 'Unknown error')],
        diagnostics: { url, errorType: err?.name || 'Unknown', errorMessage: err?.message || '' },
        suggestions: [
          'Upload the api/ folder to your web server',
          'Import api/db/schema.sql into MySQL',
          'Ensure PHP 8.1+ is active in cPanel',
          'Verify the api/ folder has the same permissions as index.html',
        ],
      });
      toast.error('Backend unreachable');
    } finally {
      setLoading(false);
    }
  }, [dbUrl]);

  useEffect(() => { checkHealth(); }, [checkHealth]);

  // ─── Load Users ───
  const loadLocalUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      // ─── Try backend first ───
      if (hasPhpBackend()) {
        try {
          const data = await apiListUsers();
          if (data.users && Array.isArray(data.users)) {
            const mapped: LocalUser[] = data.users.map((u: any) => ({
              id: String(u.id),
              username: u.username,
              name: u.name || null,
              role: u.role,
              createdAt: u.createdAt || u.created_at || new Date().toISOString(),
              inDatabase: true, // confirmed by backend query
            }));
            setLocalUsers(mapped);
            // Sync to localStorage for offline consistency
            const mockUsers = data.users.map((u: any) => ({
              username: u.username,
              password: '',
              user: {
                id: String(u.id),
                username: u.username,
                name: u.name || u.username,
                role: u.role,
                createdAt: u.created_at || new Date().toISOString(),
              },
            }));
            localStorage.setItem('rb_mock_users', JSON.stringify(mockUsers));
            return;
          }
        } catch (err) {
          console.warn('Backend list users failed, falling back to localStorage:', err);
          // Fall through to localStorage
        }
      }

      // ─── localStorage fallback ───
      const raw = localStorage.getItem('rb_mock_users');
      if (raw) {
        const parsed = JSON.parse(raw);
        const mapped: LocalUser[] = parsed.map((u: any) => ({
          id: u.user?.id || u.id || 'unknown',
          username: u.user?.username || u.username || '',
          name: u.user?.name || u.name || null,
          role: u.user?.role || u.role || 'user',
          createdAt: u.user?.createdAt || u.createdAt || new Date().toISOString(),
          inDatabase: false, // localStorage fallback — not verified in DB
        }));
        setLocalUsers(mapped);
      }
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'assignments') {
      loadLocalUsers();
    }
    if (activeTab !== 'assignments') {
      setAssignTargetUser(null);
      setSamplePool([]);
      setAssignedIds(new Set());
    }
  }, [activeTab, loadLocalUsers]);

  // ─── Save User Changes ───
  const handleSaveUser = (userId: string) => {
    try {
      const raw = localStorage.getItem('rb_mock_users');
      if (!raw) return;
      const users = JSON.parse(raw);
      const idx = users.findIndex((u: any) => (u.user?.id || u.id) === userId);
      if (idx === -1) {
        toast.error('User not found');
        return;
      }

      // Check username uniqueness (excluding current user)
      const usernameExists = users.some((u: any, i: number) => {
        if (i === idx) return false;
        const uname = u.user?.username || u.username;
        return uname === editUsername.trim();
      });
      if (usernameExists) {
        toast.error('Username already taken');
        return;
      }

      // Update user
      if (users[idx].user) {
        users[idx].user.username = editUsername.trim();
        users[idx].user.name = editName.trim() || editUsername.trim();
        users[idx].user.role = editRole;
        users[idx].username = editUsername.trim();
      } else {
        users[idx].username = editUsername.trim();
        users[idx].name = editName.trim() || editUsername.trim();
        users[idx].role = editRole;
      }

      localStorage.setItem('rb_mock_users', JSON.stringify(users));
      toast.success('User updated successfully');
      setEditingUser(null);
      loadLocalUsers();
    } catch (e) {
      toast.error('Failed to update user');
    }
  };

  // ─── Reset Password ───
  const handleResetPassword = (userId: string) => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (newPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      const raw = localStorage.getItem('rb_mock_users');
      if (!raw) return;
      const users = JSON.parse(raw);
      const idx = users.findIndex((u: any) => (u.user?.id || u.id) === userId);
      if (idx === -1) {
        toast.error('User not found');
        return;
      }
      users[idx].password = newPassword;
      localStorage.setItem('rb_mock_users', JSON.stringify(users));
      toast.success('Password reset successfully');
      setResetPwUser(null);
      setNewPassword('');
    } catch (e) {
      toast.error('Failed to reset password');
    }
  };

  // ─── Delete User ───
  const handleDeleteUser = async (userId: string) => {
    if (userId === user?.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    if (!confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    try {
      // Try backend first
      if (hasPhpBackend()) {
        try {
          await apiDeleteUser(Number(userId));
          toast.success('User deleted from database');
          loadLocalUsers();
          return;
        } catch (err) {
          console.warn('Backend delete failed, falling back to localStorage:', err);
        }
      }
      // localStorage fallback
      const raw = localStorage.getItem('rb_mock_users');
      if (!raw) return;
      const users = JSON.parse(raw);
      const filtered = users.filter((u: any) => (u.user?.id || u.id) !== userId);
      localStorage.setItem('rb_mock_users', JSON.stringify(filtered));
      toast.success('User deleted');
      loadLocalUsers();
    } catch (e) {
      toast.error('Failed to delete user');
    }
  };

  // ─── Create User ───
  const handleCreateUser = async () => {
    if (!newUserForm.username.trim() || !newUserForm.password) {
      toast.error('Username and password are required');
      return;
    }
    if (newUserForm.password.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (newUserForm.password !== newUserForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setCreatingUser(true);
    const displayName = newUserForm.name.trim() || newUserForm.username.trim();
    const country     = newUserForm.country;
    try {
      let newUserId: string | number | null = null;

      // ─── Try backend first ───
      if (hasPhpBackend()) {
        try {
          const res = await apiCreateUser({
            username: newUserForm.username.trim(),
            password: newUserForm.password,
            name: displayName,
            role: newUserForm.role,
          });
          newUserId = res?.id ?? res?.userId ?? null;
          toast.success(`User '${newUserForm.username}' created`);
          // Also save to localStorage for consistency
          const raw = localStorage.getItem('rb_mock_users');
          const users = raw ? JSON.parse(raw) : [];
          const mockId = 'mock_' + Math.random().toString(36).slice(2, 10);
          users.push({
            username: newUserForm.username.trim(),
            password: newUserForm.password,
            user: {
              id: newUserId ? String(newUserId) : mockId,
              username: newUserForm.username.trim(),
              name: displayName,
              role: newUserForm.role,
              createdAt: new Date().toISOString(),
            },
          });
          localStorage.setItem('rb_mock_users', JSON.stringify(users));
        } catch (err) {
          console.warn('Backend create user failed, falling back to localStorage:', err);
          // Fall through to localStorage
        }
      }

      // ─── localStorage fallback (also runs if backend didn't set userId) ───
      if (!newUserId) {
        const raw = localStorage.getItem('rb_mock_users');
        const users = raw ? JSON.parse(raw) : [];
        if (users.some((u: any) => (u.user?.username || u.username) === newUserForm.username.trim())) {
          toast.error('Username already taken');
          return;
        }
        const mockId = 'mock_' + Math.random().toString(36).slice(2, 10);
        newUserId = mockId;
        users.push({
          username: newUserForm.username.trim(),
          password: newUserForm.password,
          user: {
            id: mockId,
            username: newUserForm.username.trim(),
            name: displayName,
            role: newUserForm.role,
            createdAt: new Date().toISOString(),
          },
        });
        localStorage.setItem('rb_mock_users', JSON.stringify(users));
        toast.success(`User '${newUserForm.username}' created`);
      }

      // ─── Seed customer + sites + samples ───
      setSeedingUser(true);
      try {
        if (hasPhpBackend() && typeof newUserId === 'number') {
          await seedUserDataRemote(newUserId as number, country, displayName);
          toast.success(`Seeded 1 customer, 3 sites, 9 influent samples for ${displayName}`);
        } else {
          seedUserDataLocally(String(newUserId), country, displayName);
          toast.success(`Seeded demo data locally for ${displayName}`);
        }
      } catch (seedErr: any) {
        console.warn('Seeding failed, attempting local fallback:', seedErr);
        try {
          seedUserDataLocally(String(newUserId), country, displayName);
          toast.info('Demo data seeded locally (remote seed unavailable)');
        } catch (localErr) {
          console.error('Local seed also failed:', localErr);
        }
      } finally {
        setSeedingUser(false);
      }

      setNewUserForm({ username: '', password: '', confirmPassword: '', name: '', role: 'user', country: 'GB' });
      await loadLocalUsers();
    } catch (e) {
      toast.error('Failed to create user');
    } finally {
      setCreatingUser(false);
      setSeedingUser(false);
    }
  };

  // ─── Filtered Users ───
  const filteredUsers = localUsers.filter(u =>
    u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Guard: if no user loaded yet
  if (!user) {
    return (
      <div className="rb-card p-8 text-center">
        <Activity className="w-8 h-8 text-slate-600 mx-auto mb-3 animate-spin" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  // Guard: not admin
  if (user.role !== 'admin') {
    return (
      <div className="rb-card p-8 text-center">
        <Shield className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-sm font-bold text-red-400">Admin Access Required</p>
        <p className="text-xs text-slate-500 mt-1">Your role: {user.role || 'user'}</p>
        <button onClick={() => navigate('/')} className="mt-4 text-xs text-cyan-400 hover:underline">Return to Dashboard</button>
      </div>
    );
  }

  // Safe check extraction
  const allChecks = health && typeof health.checks === 'object' && health.checks !== null
    ? Object.entries(health.checks)
    : [];
  const passCount = allChecks.filter(([, v]) => v && v.pass === true).length;
  const failCount = allChecks.filter(([, v]) => !v || v.pass !== true).length;
  const errorList = Array.isArray(health?.errors) ? health.errors : [];
  const suggestionList = Array.isArray(health?.suggestions) ? health.suggestions : [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title="Admin Console"
        subtitle="Backend validation, system diagnostics, and user management"
        actions={
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-400 border border-slate-800/60 bg-slate-800/40 hover:text-slate-200 hover:bg-slate-800 transition-all"
          >
            <ArrowLeft className="w-3.5 h-3.5" />Back to Dashboard
          </button>
        }
      />

      {/* Tab Navigation */}
      <div className="flex items-center gap-0 border-b border-slate-800/60">
        <button
          onClick={() => setActiveTab('health')}
          className={`flex items-center gap-2 px-5 py-3 text-[11px] font-bold border-b-2 -mb-px transition-all ${
            activeTab === 'health'
              ? 'border-rb-brand text-rb-brand'
              : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'
          }`}
        >
          <Database className="w-3.5 h-3.5" />Backend Health
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 px-5 py-3 text-[11px] font-bold border-b-2 -mb-px transition-all ${
            activeTab === 'users'
              ? 'border-rb-brand text-rb-brand'
              : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'
          }`}
        >
          <Users className="w-3.5 h-3.5" />User Management
          <span className="px-1.5 py-0.5 rounded-full bg-slate-700/60 text-slate-400 text-[9px] font-bold">{localUsers.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('assignments')}
          className={`flex items-center gap-2 px-5 py-3 text-[11px] font-bold border-b-2 -mb-px transition-all ${
            activeTab === 'assignments'
              ? 'border-sky-500 text-sky-400'
              : 'border-transparent text-slate-500 hover:text-slate-300 hover:border-slate-600'
          }`}
        >
          <Share2 className="w-3.5 h-3.5" />Sample Assignments
        </button>
        <button
          onClick={() => navigate('/admin/demo-data')}
          className="flex items-center gap-2 px-5 py-3 text-[11px] font-bold border-b-2 -mb-px border-transparent text-slate-500 hover:text-violet-300 hover:border-violet-500/40 transition-all"
        >
          <FlaskConical className="w-3.5 h-3.5" />Demo Data<ExternalLink className="w-3 h-3 opacity-50" />
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* BACKEND HEALTH TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'health' && (
        <div className="space-y-6">
          {/* Backend URL */}
          <div className="rb-card p-4">
            <label className="text-[10px] text-slate-500 uppercase font-bold block mb-2">Backend API URL</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={dbUrl}
                onChange={(e) => setDbUrl(e.target.value)}
                placeholder="e.g. https://yourserver.com/api or /api"
                className="flex-1 px-3 py-2 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
              />
              <button
                onClick={checkHealth}
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Checking...' : 'Validate Backend'}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              Use <code className="text-cyan-400">/api</code> if frontend and backend share a domain, or full URL otherwise.
            </p>
          </div>

          {/* Status Banner */}
          {health && (
            <div className={`p-4 rounded-xl border flex items-start gap-3 ${
              health.status === 'healthy' ? 'bg-emerald-500/5 border-emerald-500/20' :
              health.status === 'unreachable' ? 'bg-red-500/5 border-red-500/20' :
              'bg-amber-500/5 border-amber-500/20'
            }`}>
              {health.status === 'healthy' ? <CheckCircle2 className="w-6 h-6 text-emerald-400 mt-0.5" /> :
               health.status === 'unreachable' ? <WifiOff className="w-6 h-6 text-red-400 mt-0.5" /> :
               <AlertTriangle className="w-6 h-6 text-amber-400 mt-0.5" />}
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <p className={`text-sm font-bold ${
                    health.status === 'healthy' ? 'text-emerald-400' :
                    health.status === 'unreachable' ? 'text-red-400' :
                    'text-amber-400'
                  }`}>
                    Backend: {health.status === 'healthy' ? 'Healthy' : health.status === 'unreachable' ? 'Unreachable' : 'Degraded'}
                  </p>
                  {health.responseTimeMs > 0 && (
                    <span className="text-[10px] text-slate-500">{health.responseTimeMs}ms</span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-[10px]">
                  <span className="text-emerald-400">{passCount} passed</span>
                  <span className="text-slate-600">·</span>
                  <span className={failCount > 0 ? 'text-red-400' : 'text-slate-500'}>{failCount} failed</span>
                  <span className="text-slate-600">·</span>
                  <span className="text-slate-500">{allChecks.length} checks</span>
                </div>
              </div>
            </div>
          )}

          {/* Individual Checks */}
          {allChecks.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {allChecks.map(([key, value]) => {
                const passed = !!value && value.pass === true;
                const valStr = value && value.value !== undefined ? String(value.value) : '—';
                return (
                  <div
                    key={key}
                    className={`p-3 rounded-xl border flex items-center gap-3 ${
                      passed ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'
                    }`}
                  >
                    {passed ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white capitalize">{key.replace(/[_]/g, ' ')}</p>
                      <p className="text-[10px] text-slate-400">{valStr}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Errors */}
          {errorList.length > 0 && (
            <div className="rb-card p-4 border-red-500/20">
              <h3 className="text-sm font-bold text-red-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Issues ({errorList.length})
              </h3>
              <div className="space-y-3">
                {errorList.map((err, i) => (
                  <div key={i} className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                    <p className="text-xs font-bold text-red-400">{err}</p>
                    {suggestionList[i] && (
                      <p className="text-[10px] text-slate-400 mt-1">Fix: {suggestionList[i]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Diagnostics (unreachable only) */}
          {health?.status === 'unreachable' && health.diagnostics && (
            <div className="rb-card p-4 border-red-500/20 bg-red-500/5">
              <h3 className="text-sm font-bold text-red-400 mb-3 flex items-center gap-2">
                <Terminal className="w-4 h-4" /> Raw Server Response
              </h3>
              <div className="space-y-2 text-xs font-mono text-slate-300">
                <div className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">URL:</span><span className="text-cyan-400 break-all">{String(health.diagnostics.url || '')}</span></div>
                <div className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">Status:</span><span className={health.diagnostics.status === 200 ? 'text-red-400' : 'text-amber-400'}>{String(health.diagnostics.status || '')}</span></div>
                <div className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">Content-Type:</span><span>{String(health.diagnostics.contentType || '')}</span></div>
                <div className="flex gap-2"><span className="text-slate-500 w-24 shrink-0">Is HTML:</span><span className={health.diagnostics.isHtml ? 'text-red-400 font-bold' : 'text-emerald-400'}>{health.diagnostics.isHtml ? 'YES — server is rewriting to index.html' : 'No'}</span></div>
                <div className="mt-2"><span className="text-slate-500">Body preview:</span></div>
                <pre className="p-2 rounded-lg bg-slate-900/80 border border-slate-700 text-[10px] text-slate-400 overflow-x-auto whitespace-pre-wrap">{String(health.diagnostics.bodyPreview || '')}</pre>
              </div>
            </div>
          )}

          {/* Deployment Guide (unreachable only) */}
          {(!health || health.status === 'unreachable') && (
            <div className="rb-card p-5">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Terminal className="w-4 h-4 text-rb-brand" /> Quick Deployment Guide
              </h3>
              <div className="space-y-2 text-xs text-slate-400">
                {[
                  'Upload the api/ folder to your web server alongside index.html',
                  'Import api/db/schema.sql into MySQL: mysql -u root -p < schema.sql',
                  'Create api/.env from .env.example with your DB credentials',
                  'Enable mod_rewrite: a2enmod rewrite; set AllowOverride All',
                  'Click Validate Backend above to verify the connection',
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-rb-brand/10 text-rb-brand flex items-center justify-center shrink-0 text-[10px] font-bold">{i + 1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Info */}
          {health && health.status !== 'unreachable' && (
            <div className="rb-card p-4">
              <h3 className="text-sm font-bold text-white mb-3">System Info</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'PHP', value: health.phpVersion || health.checks?.php_version?.value || '—', icon: Cpu },
                  { label: 'DB Host', value: health.checks?.db_host?.value || '—', icon: Server },
                  { label: 'DB Name', value: health.checks?.db_name?.value || '—', icon: Database },
                  { label: 'DB Status', value: health.checks?.connection?.pass === true ? 'Connected' : health.checks?.connection?.value || 'Failed', icon: Activity },
                  { label: 'JWT', value: health.checks?.jwt_secret?.value || '—', icon: Lock },
                  { label: 'Latency', value: health.responseTimeMs !== undefined ? `${health.responseTimeMs}ms` : '—', icon: Activity },
                ].map((item, i) => (
                  <div key={i} className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
                    <item.icon className="w-4 h-4 text-slate-500 mb-1" />
                    <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                    <p className="text-sm font-bold text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Database Tables */}
          {health?.checks?.all_tables && (
            <div className="rb-card p-4">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Table className="w-4 h-4 text-rb-brand" /> Database Tables
              </h3>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const raw = health.checks.all_tables.value || '';
                  const match = raw.match(/^(\d+) tables: (.+)$/);
                  if (!match) return <p className="text-xs text-slate-400">{raw}</p>;
                  const count = match[1];
                  const tables = match[2].split(', ').map(t => t.trim()).filter(Boolean);
                  return (
                    <>
                      <p className="text-xs text-slate-400 w-full mb-2">{count} tables found in <span className="text-cyan-400 font-medium">{health.checks.db_name?.value || 'database'}</span></p>
                      {tables.map((table, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-slate-800/60 border border-slate-800/50 text-slate-300"
                        >
                          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                          {table}
                        </span>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* USER MANAGEMENT TAB */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Create User Form */}
          <div className="rb-card p-4">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-rb-brand" /> Create New User
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Username</label>
                <input
                  type="text"
                  value={newUserForm.username}
                  onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                  placeholder="e.g. john.doe"
                  className="w-full px-3 py-2 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Full Name</label>
                <input
                  type="text"
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                  placeholder="e.g. John Doe"
                  className="w-full px-3 py-2 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Password</label>
                <PasswordInput
                  value={newUserForm.password}
                  onChange={(v) => setNewUserForm({ ...newUserForm, password: v })}
                  placeholder="Min 4 characters"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Confirm Password</label>
                <PasswordInput
                  value={newUserForm.confirmPassword}
                  onChange={(v) => setNewUserForm({ ...newUserForm, confirmPassword: v })}
                  placeholder="Re-type password"
                  error={!!(newUserForm.confirmPassword && newUserForm.password !== newUserForm.confirmPassword)}
                />
                {newUserForm.confirmPassword && newUserForm.password !== newUserForm.confirmPassword && (
                  <p className="text-[10px] text-rose-400 mt-1">Passwords do not match</p>
                )}
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Role</label>
                <select
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value as 'user' | 'admin' })}
                  className="w-full px-3 py-2 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">
                    Country <span className="text-slate-600 normal-case font-normal">(sets regulation + demo data)</span>
                  </label>
                  <select
                    value={newUserForm.country}
                    onChange={(e) => setNewUserForm({ ...newUserForm, country: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                  >
                    <option value="GB">🇬🇧 United Kingdom (UK EA)</option>
                    <option value="AU">🇦🇺 Australia (AU EPA)</option>
                    <option value="DE">🇩🇪 Germany (EU UWWTD)</option>
                    <option value="FR">🇫🇷 France (EU UWWTD)</option>
                    <option value="NL">🇳🇱 Netherlands (EU UWWTD)</option>
                    <option value="US">🇺🇸 United States (US EPA)</option>
                    <option value="CA">🇨🇦 Canada (US EPA)</option>
                    <option value="NZ">🇳🇿 New Zealand (AU EPA)</option>
                    <option value="IE">🇮🇪 Ireland (UK EA)</option>
                  </select>
                </div>
                <button
                  onClick={handleCreateUser}
                  disabled={creatingUser || seedingUser}
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {seedingUser ? 'Seeding...' : creatingUser ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>

          {/* User List */}
          <div className="rb-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-rb-brand" /> User List ({filteredUsers.length})
              </h3>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users..."
                  className="pl-9 pr-3 py-1.5 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none w-48"
                />
              </div>
            </div>

            {loadingUsers ? (
              <div className="text-center py-8">
                <Activity className="w-6 h-6 text-slate-600 mx-auto mb-2 animate-spin" />
                <p className="text-xs text-slate-500">Loading users...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                <p className="text-xs text-slate-500">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800/50">
                      <th className="text-left text-[10px] text-slate-500 uppercase font-bold py-2 px-3">User</th>
                      <th className="text-left text-[10px] text-slate-500 uppercase font-bold py-2 px-3">Role</th>
                      <th className="text-center text-[10px] text-slate-500 uppercase font-bold py-2 px-3">DB</th>
                      <th className="text-left text-[10px] text-slate-500 uppercase font-bold py-2 px-3">Created</th>
                      <th className="text-right text-[10px] text-slate-500 uppercase font-bold py-2 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                        {/* User column */}
                        <td className="py-3 px-3">
                          {editingUser === u.id ? (
                            <div className="space-y-2">
                              <div>
                                <label className="text-[10px] text-slate-500 uppercase">Username</label>
                                <input
                                  type="text"
                                  value={editUsername}
                                  onChange={(e) => setEditUsername(e.target.value)}
                                  className="w-full px-2 py-1 rounded text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] text-slate-500 uppercase">Name</label>
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  className="w-full px-2 py-1 rounded text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-[10px] text-slate-500 uppercase">Role</label>
                                <select
                                  value={editRole}
                                  onChange={(e) => setEditRole(e.target.value as 'user' | 'admin')}
                                  className="w-full px-2 py-1 rounded text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                                >
                                  <option value="user">User</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm font-medium text-white">{u.username}</p>
                              <p className="text-[10px] text-slate-500">{u.name || '—'}</p>
                            </div>
                          )}
                        </td>

                        {/* Role */}
                        <td className="py-3 px-3">
                          {editingUser === u.id ? null : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                              u.role === 'admin'
                                ? 'bg-rb-brand/10 text-rb-brand border-rb-brand/20'
                                : 'bg-slate-800 text-slate-400 border-slate-700'
                            }`}>
                              {u.role === 'admin' ? <Shield className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                              {u.role}
                            </span>
                          )}
                        </td>

                        {/* DB Status */}
                        <td className="py-3 px-3 text-center">
                          {editingUser === u.id ? null : (
                            <div className="group relative inline-flex">
                              {u.inDatabase === true ? (
                                <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                                </div>
                              ) : u.inDatabase === false ? (
                                <div className="w-5 h-5 rounded-full bg-rose-500/20 border border-rose-500/40 flex items-center justify-center">
                                  <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                                </div>
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center">
                                  <div className="w-2.5 h-2.5 rounded-full bg-slate-500" />
                                </div>
                              )}
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 rounded bg-rb-surface border border-slate-700 text-[10px] text-slate-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                {u.inDatabase === true ? 'Confirmed in MySQL database' : u.inDatabase === false ? 'LocalStorage only — not in DB' : 'Unknown status'}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Created */}
                        <td className="py-3 px-3">
                          {editingUser === u.id ? null : (
                            <span className="text-xs text-slate-500">
                              {new Date(u.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-3">
                          <div className="flex items-center justify-end gap-1">
                            {editingUser === u.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveUser(u.id)}
                                  className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                                  title="Save"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingUser(null)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-700/30 transition-colors"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingUser(u.id);
                                    setEditUsername(u.username);
                                    setEditName(u.name || '');
                                    setEditRole(u.role as 'user' | 'admin');
                                  }}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setResetPwUser(u.id);
                                    setNewPassword('');
                                  }}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                                  title="Reset Password"
                                >
                                  <Lock className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Password Reset Modal (inline) */}
          {/* ── Assignments quick-link inside user tab ── */}
          <div className="rb-card p-4 border-sky-500/20 bg-sky-500/5">
            <p className="text-xs text-sky-300 flex items-center gap-2">
              <Share2 className="w-4 h-4 shrink-0" />
              To assign demo samples to a specific user, use the{' '}
              <button onClick={() => setActiveTab('assignments')} className="underline font-bold hover:text-sky-200">
                Sample Assignments
              </button>{' '}tab.
            </p>
          </div>

          {resetPwUser && (
            <div className="rb-card p-4 border-amber-500/20">
              <h3 className="text-sm font-bold text-amber-400 mb-3 flex items-center gap-2">
                <Lock className="w-4 h-4" /> Reset Password
              </h3>
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 4 chars)"
                    className="w-full px-3 py-2 pr-10 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-rb-brand outline-none"
                  />
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={() => handleResetPassword(resetPwUser)}
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20 hover:bg-amber-500/25 transition-all"
                >
                  Reset
                </button>
                <button
                  onClick={() => { setResetPwUser(null); setNewPassword(''); setResetConfirmPassword(''); }}
                  className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SAMPLE ASSIGNMENTS TAB                                  */}
      {/* ═══════════════════════════════════════════════════════ */}
      {activeTab === 'assignments' && (
        <div className="space-y-5">

          {/* Header */}
          <div className="rb-card p-5">
            <div className="flex items-start gap-3">
              <Share2 className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-white mb-1">Assign Demo Samples to Users</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Select a user on the left, then tick the samples from the pool you want to make
                  available to them. Their own samples are always visible — this controls which
                  additional demo or shared samples appear in their library.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* ── Left: User list ── */}
            <div className="rb-card p-4 space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Select User
              </h4>
              {loadingUsers ? (
                <p className="text-xs text-slate-500 animate-pulse">Loading users…</p>
              ) : (
                <div className="space-y-1.5 max-h-[480px] overflow-y-auto scrollbar-thin pr-1">
                  {localUsers.filter(u => u.role !== 'admin').length === 0 && (
                    <p className="text-xs text-slate-600 italic">No non-admin users found.</p>
                  )}
                  {localUsers.filter(u => u.role !== 'admin').map(u => {
                    const isSelected = assignTargetUser?.id === u.id;
                    return (
                      <button
                        key={u.id}
                        onClick={() => {
                          setAssignTargetUser(u);
                          loadAssignments(u);
                          setPoolSearch('');
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all flex items-center gap-2 ${
                          isSelected
                            ? 'bg-sky-500/15 border-sky-500/30 text-sky-300'
                            : 'bg-slate-800/30 border-slate-800/30 text-slate-300 hover:border-sky-500/20 hover:bg-sky-500/5'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full shrink-0 ${isSelected ? 'bg-sky-400' : 'bg-slate-600'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{u.username}</p>
                          {u.name && <p className="text-[10px] text-slate-500 truncate">{u.name}</p>}
                        </div>
                        {isSelected && <ChevronRight className="w-4 h-4 text-sky-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Right: Sample pool ── */}
            <div className="lg:col-span-2 space-y-4">
              {!assignTargetUser ? (
                <div className="rb-card p-8 text-center">
                  <BookOpen className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Select a user on the left to manage their sample assignments.</p>
                </div>
              ) : loadingPool ? (
                <div className="rb-card p-8 text-center">
                  <RefreshCw className="w-8 h-8 text-slate-600 mx-auto mb-3 animate-spin" />
                  <p className="text-xs text-slate-500 animate-pulse">Loading sample pool…</p>
                </div>
              ) : (
                <>
                  {/* Toolbar */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[180px] relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                      <input
                        value={poolSearch}
                        onChange={e => setPoolSearch(e.target.value)}
                        placeholder="Search samples…"
                        className="w-full pl-8 pr-3 py-2 rounded-lg text-sm text-white bg-slate-800 border border-slate-700 focus:border-sky-500 outline-none"
                      />
                    </div>
                    <span className="text-xs text-slate-500 shrink-0">
                      {assignedIds.size} selected / {samplePool.length} in pool
                    </span>
                    <button
                      onClick={saveAssignments}
                      disabled={savingAssignments}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30 hover:bg-sky-500/30 transition-all disabled:opacity-50"
                    >
                      {savingAssignments
                        ? <RefreshCw className="w-4 h-4 animate-spin" />
                        : <Save className="w-4 h-4" />}
                      Save Assignments
                    </button>
                  </div>

                  {samplePool.length === 0 ? (
                    <div className="rb-card p-6 text-center space-y-2">
                      <BookOpen className="w-10 h-10 text-slate-400 mx-auto" />
                      <p className="text-sm text-slate-400">No samples exist in the system yet.</p>
                      <p className="text-xs text-slate-600">
                        Create the Tasman demo account (Admin → Demo Data) or seed data for any user — then return here to assign samples.
                      </p>
                    </div>
                  ) : (
                    (() => {
                      const targetId = assignTargetUser!.id;
                      // Group by owner username
                      const filtered = samplePool.filter(s =>
                        !poolSearch ||
                        s.sampleName.toLowerCase().includes(poolSearch.toLowerCase()) ||
                        (s.customerName || '').toLowerCase().includes(poolSearch.toLowerCase()) ||
                        s.ownerUsername.toLowerCase().includes(poolSearch.toLowerCase()) ||
                        (s.reportNumber || '').toLowerCase().includes(poolSearch.toLowerCase())
                      );
                      const groups: Record<string, SamplePoolItem[]> = {};
                      for (const s of filtered) {
                        (groups[s.ownerUsername] ??= []).push(s);
                      }
                      return Object.entries(groups).map(([owner, samples]) => {
                        // Samples the target user already owns — can't be "assigned", already visible
                        const ownSampleIds      = samples.filter(s => String(s.ownerUserId) === targetId).map(s => s.id);
                        const assignableSampleIds = samples.filter(s => String(s.ownerUserId) !== targetId).map(s => s.id);
                        const allChecked  = assignableSampleIds.length > 0 && assignableSampleIds.every(id => assignedIds.has(id));
                        const someChecked = assignableSampleIds.some(id => assignedIds.has(id));
                        const isExpanded  = expandedOwners.has(owner);
                        const isTargetOwner = ownSampleIds.length === samples.length; // this group IS the target user

                        return (
                          <div key={owner} className="rb-card overflow-hidden">
                            {/* Group header */}
                            <div className={`flex items-center gap-3 px-4 py-3 border-b border-slate-800/40 ${isTargetOwner ? 'bg-emerald-500/5' : 'bg-slate-800/30'}`}>
                              {!isTargetOwner && (
                                <input
                                  type="checkbox"
                                  checked={allChecked}
                                  ref={(el: HTMLInputElement | null) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                  onChange={() => toggleOwnerGroup(owner, assignableSampleIds)}
                                  className="w-4 h-4 rounded accent-sky-400 cursor-pointer"
                                />
                              )}
                              {isTargetOwner && (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                              )}
                              <button
                                onClick={() => setExpandedOwners(prev => {
                                  const next = new Set(prev);
                                  next.has(owner) ? next.delete(owner) : next.add(owner);
                                  return next;
                                })}
                                className="flex-1 text-left flex items-center gap-2"
                              >
                                {isExpanded
                                  ? <ChevronDown className="w-4 h-4 text-slate-500" />
                                  : <ChevronRight className="w-4 h-4 text-slate-500" />}
                                <span className={`text-sm font-bold ${isTargetOwner ? 'text-emerald-400' : 'text-white'}`}>{owner}</span>
                                {isTargetOwner
                                  ? <span className="text-[10px] text-emerald-600 font-medium">already visible to this user</span>
                                  : <span className="text-[10px] text-slate-500 font-mono">
                                      {assignableSampleIds.filter(id => assignedIds.has(id)).length}/{samples.length} selected
                                    </span>
                                }
                              </button>
                            </div>

                            {/* Sample rows */}
                            {isExpanded && (
                              <div className="divide-y divide-slate-800/40">
                                {samples.map(s => {
                                  const isOwn    = String(s.ownerUserId) === targetId;
                                  const checked  = assignedIds.has(s.id);
                                  return (
                                    <label
                                      key={s.id}
                                      className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                                        isOwn    ? 'opacity-50 cursor-default' :
                                        checked  ? 'bg-sky-500/5 cursor-pointer' :
                                        'hover:bg-slate-800/20 cursor-pointer'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isOwn || checked}
                                        disabled={isOwn}
                                        onChange={() => !isOwn && toggleSample(s.id)}
                                        className="w-4 h-4 rounded accent-sky-400 mt-0.5 shrink-0"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${isOwn ? 'text-slate-500' : checked ? 'text-sky-200' : 'text-white'}`}>
                                          {s.sampleName}
                                        </p>
                                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                          {s.customerName && (
                                            <span className="text-[10px] text-slate-500">{s.customerName}</span>
                                          )}
                                          {s.siteName && (
                                            <span className="text-[10px] text-slate-600">· {s.siteName}</span>
                                          )}
                                          {s.reportNumber && (
                                            <span className="text-[10px] font-mono text-slate-600">{s.reportNumber}</span>
                                          )}
                                          {isOwn && (
                                            <span className="text-[10px] text-emerald-400 font-medium">own sample</span>
                                          )}
                                        </div>
                                      </div>
                                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded border shrink-0 ${
                                        s.standard === 'AU_EPA'   ? 'text-emerald-400 border-emerald-500/20 bg-emerald-500/10' :
                                        s.standard === 'UK_EA'    ? 'text-sky-400 border-sky-500/20 bg-sky-500/10' :
                                        s.standard === 'US_EPA'   ? 'text-rose-400 border-rose-500/20 bg-rose-500/10' :
                                        'text-violet-400 border-violet-500/20 bg-violet-500/10'
                                      }`}>
                                        {s.standard || '—'}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()
                  )}

                  {/* Bottom save */}
                  {samplePool.length > 0 && (
                    <div className="flex justify-end">
                      <button
                        onClick={saveAssignments}
                        disabled={savingAssignments}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold bg-gradient-to-r from-sky-600 to-sky-500 text-white shadow-lg shadow-sky-500/20 hover:from-sky-500 hover:to-sky-400 transition-all disabled:opacity-50"
                      >
                        {savingAssignments ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save — assign {assignedIds.size} sample{assignedIds.size !== 1 ? 's' : ''} to {assignTargetUser.username}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
